import { Controller } from "@hotwired/stimulus"

// Auto-dismiss flash toast notifications
//
// Positions itself just left of the header's right-hand group (setup guide
// label, chat button, company logo - data-header-right), centered
// vertically, so it never covers them (Phelipe 2026-10-09: "Project created
// successfully!" sat on top of the Set up your project label). Falls back
// to the logo alone, then to top-right.

export default class extends Controller {
  static values = {
    dismissAfter: { type: Number, default: 5000 }
  }

  connect() {
    // Remove any existing toasts so they don't overlap
    document.querySelectorAll("[data-controller='flash']").forEach(el => {
      if (el !== this.element) el.remove()
    })

    this.element.style.cursor = "pointer"
    this.element.style.transform = "translateX(-50px)"

    // Position after the page is fully loaded so the logo element is available
    if (document.readyState === "complete") {
      this._positionAndReveal()
    } else {
      window.addEventListener("load", () => this._positionAndReveal(), { once: true })
    }

    // Click anywhere to dismiss immediately
    this._onClick = () => this.dismiss()
    this.element.addEventListener("click", this._onClick)

    // Pause timer on hover, resume on leave
    this._onEnter = () => this._pauseTimer()
    this._onLeave = () => this._startTimer()
    this.element.addEventListener("mouseenter", this._onEnter)
    this.element.addEventListener("mouseleave", this._onLeave)

    // Auto-dismiss
    this._startTimer()
  }

  disconnect() {
    this._clearTimer()
    this.element.removeEventListener("click", this._onClick)
    this.element.removeEventListener("mouseenter", this._onEnter)
    this.element.removeEventListener("mouseleave", this._onLeave)
  }

  dismiss() {
    this._clearTimer()

    // Fade out
    this.element.style.opacity = "0"

    // Remove from DOM after animation
    setTimeout(() => {
      this.element.remove()
    }, 300)
  }

  _positionAndReveal() {
    requestAnimationFrame(() => {
      this._position()
      this.element.style.opacity = "1"
      this.element.style.transform = "translateX(0)"
    })
  }

  _position() {
    const logo = document.querySelector("[data-header-right]") || document.querySelector("[data-company-logo]")
    if (logo) {
      const logoRect = logo.getBoundingClientRect()
      const toastHeight = this.element.offsetHeight || 32
      // Center vertically on the header (50px)
      const headerHeight = 50
      const top = (headerHeight - toastHeight) / 2

      this.element.style.top = `${Math.max(top, 4)}px`
      this.element.style.right = "auto"
      this.element.style.left = `${logoRect.left - this.element.offsetWidth - 12}px`
    } else {
      this.element.style.top = "0.625rem"
      this.element.style.right = "1rem"
    }
  }

  _startTimer() {
    this._clearTimer()
    if (this.dismissAfterValue > 0) {
      this._timeout = setTimeout(() => this.dismiss(), this.dismissAfterValue)
    }
  }

  _pauseTimer() {
    this._clearTimer()
  }

  _clearTimer() {
    if (this._timeout) {
      clearTimeout(this._timeout)
      this._timeout = null
    }
  }
}
